import { isElectron } from '@fluxter/shared';

/**
 * Bootstrap-time hooks that adapt the SPA to its host shell.
 *
 * Two responsibilities, both written to the document element so
 * stylesheets can key off attributes without React state:
 *
 *  1. **Platform marker** — `data-platform="electron" | "web"` lets
 *     CSS rules differentiate between web and desktop without a
 *     JS check at render time. Used by `styles.css` to:
 *       - revert button cursors to the arrow (`cursor: default`),
 *         matching native macOS apps where buttons don't show the
 *         "hand" cursor.
 *
 *  2. **Window focus tracking** — `data-window-focused="true" | "false"`
 *     mirrors the BrowserWindow's focus state. Subscribed via the
 *     preload's `onWindowFocusChanged` bridge. Used by `styles.css`
 *     to dim the chrome (sidebar, top bar) when Fluxter is in the
 *     background — same pattern as Mail / Slack / Linear.
 *
 * Web users see neither attribute applied beyond the platform stamp;
 * the focus tracking is no-op there since browser focus is the
 * page's concern, not the window chrome's.
 */

interface NativeShellBridge {
  onWindowFocusChanged?: (cb: (focused: boolean) => void) => () => void;
}

const bridge = (): NativeShellBridge | null => {
  if (!isElectron()) return null;
  const fluxter = (window as unknown as { fluxter?: NativeShellBridge }).fluxter;
  return fluxter ?? null;
};

const setFocused = (focused: boolean): void => {
  document.documentElement.dataset.windowFocused = focused ? 'true' : 'false';
};

/**
 * Run once at app boot, before React mounts. Idempotent — safe to
 * call multiple times (re-stamping the same attributes is a no-op).
 *
 * Returns nothing; the cleanup of the focus subscription is owned
 * by the desktop main process (the bridge keeps a single listener
 * for the renderer's lifetime).
 */
export const initNativeShell = (): void => {
  document.documentElement.dataset.platform = isElectron() ? 'electron' : 'web';

  // Default to focused on initial paint; the BrowserWindow comes to
  // front on launch in the overwhelming majority of cases. Any
  // mismatch corrects itself on the first focus / blur event.
  setFocused(true);

  const fluxterBridge = bridge();
  if (fluxterBridge?.onWindowFocusChanged) {
    fluxterBridge.onWindowFocusChanged(setFocused);
  }
};
